import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRightIcon, 
  MusicalNoteIcon, 
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

interface Song {
  id: number;
  title: string;
  releaseDate: string;
  streams: number;
  previousStreams: number;
  score: number;
  duration: string;
}

interface SongListProps {
  songs: Song[];
  artistId: string;
}

const SongList: React.FC<SongListProps> = ({ songs, artistId }) => {
  const [sortField, setSortField] = useState<'title' | 'releaseDate' | 'streams' | 'score'>('streams');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [displayLimit, setDisplayLimit] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleSort = (field: 'title' | 'releaseDate' | 'streams' | 'score') => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter songs based on search query
  const filteredSongs = songs.filter(song => 
    song.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedSongs = [...filteredSongs].sort((a, b) => {
    let comparison = 0;
    if (sortField === 'title') {
      comparison = a.title.localeCompare(b.title);
    } else if (sortField === 'releaseDate') {
      comparison = new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
    } else if (sortField === 'streams') {
      comparison = a.streams - b.streams;
    } else if (sortField === 'score') {
      comparison = a.score - b.score;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const getTrendIcon = (current: number, previous: number) => {
    const difference = current - previous;
    if (difference > 0) {
      return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    } else if (difference < 0) {
      return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    }
    return <MinusIcon className="h-4 w-4 text-gray-400" />;
  };

  // Get songs to display based on the current limit
  const displayedSongs = sortedSongs.slice(0, displayLimit);

  // Show more/less logic
  const handleShowMore = () => {
    setDisplayLimit(prev => prev + 10);
  };

  const handleShowLess = () => {
    setDisplayLimit(10);
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Songs Performance</h3>
        <div className="relative">
          <input
            type="text"
            placeholder="Search songs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 pr-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
          />
          <svg 
            className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('title')}
              >
                Song
                {sortField === 'title' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('releaseDate')}
              >
                Release
                {sortField === 'releaseDate' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('streams')}
              >
                Streams
                {sortField === 'streams' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('score')}
              >
                Score
                {sortField === 'score' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedSongs.map((song) => (
              <tr key={song.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <MusicalNoteIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="text-sm font-medium text-gray-900">{song.title}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {new Date(song.releaseDate).toLocaleDateString('de-DE', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-900">
                      {song.streams.toLocaleString()}
                    </div>
                    <span className="ml-2">
                      {getTrendIcon(song.streams, song.previousStreams)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${song.score >= 80 ? 'bg-green-100 text-green-800' : 
                    song.score >= 60 ? 'bg-green-100 text-green-800' : 
                    song.score >= 40 ? 'bg-yellow-100 text-yellow-800' : 
                    song.score >= 20 ? 'bg-orange-100 text-orange-800' : 
                    'bg-red-100 text-red-800'}`}>
                    {song.score}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Link 
                    to={`/artist/${artistId}/song/${song.id}`} 
                    className="text-green-600 hover:text-green-900"
                  >
                    <span className="flex items-center justify-end">
                      Details
                      <ChevronRightIcon className="ml-1 h-4 w-4" />
                    </span>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Show more/less button */}
      {sortedSongs.length > 10 && (
        <div className="mt-4 flex justify-center">
          {displayLimit < sortedSongs.length ? (
            <button
              onClick={handleShowMore}
              className="flex items-center px-4 py-2 text-sm text-green-600 hover:text-green-800"
            >
              Show More <ChevronDownIcon className="ml-1 h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleShowLess}
              className="flex items-center px-4 py-2 text-sm text-green-600 hover:text-green-800"
            >
              Show Less <ChevronUpIcon className="ml-1 h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Display song count info */}
      <div className="mt-2 text-sm text-gray-500 text-center">
        Showing {displayedSongs.length} of {sortedSongs.length} songs
        {searchQuery && ` (filtered from ${songs.length} total)`}
      </div>
    </div>
  );
};

export default SongList;